import { Injectable } from '@angular/core';
import {
  addDoc,
  deleteDoc,
  collection,
  Firestore,
  getDocs,
  query,
  where,
  Query,
  QuerySnapshot,
  DocumentData,
  doc
} from "@angular/fire/firestore";
import {from, map, Observable} from "rxjs";
import {Registration} from "../models/registration";
import {EmailService} from "./email.service";

@Injectable({
  providedIn: 'root'
})
export class RegistrationService {

  constructor(
    private firestore: Firestore,
    private mailService: EmailService
  ) { }

  /**
   * Adds a new registration to an event.
   *
   * @param eventID
   * @param userID
   * @return document id of newly created registration
   */
  async addRegistration (eventID: string, userID: string): Promise<any> {
    const userRef = doc(this.firestore, `users/${userID}`);
    const eventRef = doc(this.firestore, `events/${eventID}`);

    return await addDoc(collection(this.firestore, 'registrations'), {
      user: userRef,
      event: eventRef
    }).then(docRef => {
      docRef.id;
      this.mailService.sendRegistrationEmail(eventID, userID);
    });
  }

  /**
   * Removes registration from an event.
   *
   * @param eventID
   * @param userID
   * @return document id of newly created registration
   */
  async removeRegistration (eventID: string, userID: string): Promise<any> {
    const userRef = doc(this.firestore, `users/${userID}`);
    const eventRef = doc(this.firestore, `events/${eventID}`);

    const registrationsQuery = query(
      collection(this.firestore, 'registrations'),
      where('user', '==', userRef),
      where('event', '==', eventRef)
    ) as Query<DocumentData>;

    const docSnap = await getDocs(registrationsQuery);

    return docSnap.forEach((doc => {
      deleteDoc(doc.ref);
    }));
  }

  /**
   * Returns all the registrations of the provided event
   *
   * @param eventID
   * @return array of registrations
   */
  public getRegistrationsByEvent(eventID: string): Observable<Registration[]> {
    const eventRef = doc(this.firestore, `events/${eventID}`)

    const eventsQuery = query(
      collection(this.firestore, 'registrations'),
      where('event', '==', eventRef)
    ) as Query<DocumentData>;

    return from(getDocs(eventsQuery)).pipe(
      map((snapshot: QuerySnapshot<DocumentData>) => snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }) as Registration))
    );
  }

  /**
   * Returns all the registrations of the provided user
   *
   * @param userID
   * @return array of registrations
   */
  public getRegistrationsByUser(userID: string): Observable<Registration[]> {
    const userRef = doc(this.firestore, `users/${userID}`);

    const registrationQuery = query(
      collection(this.firestore, 'registrations'),
      where('user', '==', userRef)
    ) as Query<DocumentData>;

    return from(getDocs(registrationQuery)).pipe(
      map((snapshot: QuerySnapshot<DocumentData>) => snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }) as Registration))
    );
  }

  /**
   * Checks if the provided user has already registered for an event, returns true if applicable.
   *
   * @param userID
   * @param eventID
   * @return true if user is registered for event
   */
  public async checkUserRegistered(userID: string, eventID: string): Promise<boolean> {
    const userRef = doc(this.firestore, `users/${userID}`);
    const eventRef = doc(this.firestore, `events/${eventID}`);

    const registrationQuery = query(
      collection(this.firestore, 'registrations'),
      where('user', '==', userRef),
      where("event", "==", eventRef)
    ) as Query<DocumentData>;

    try {
      const querySnapshot = await getDocs(registrationQuery);
      const count = querySnapshot.size;

      return count >= 1;
    } catch (error) {
      console.error("Error executing query and counting results:", error);
      return false;
    }
  }
}
